import React, { Component } from 'react';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../../utils';
import { withTheme } from 'styled-components';
import { utils } from '@kurtosys/ksys-app-template';
import { Sup } from './StyledComponents';
import { SuperscriptProps, SuperscriptMetaWithRef } from './Models';
import { generateGuid } from '../../utils/generateGuid';
import { KeyboardEventHelper } from '../../utils/KeyboardEventHelper';
import { SuperscriptRefHelper } from '../../utils/SuperscriptRefHelper';
@inject('accessibilityStore', 'dataStore', 'entityStore')
@observer
class Superscript extends Component<SuperscriptProps> {
    @computed
    get accessibilityStore() {
        const { accessibilityStore } = this.props;
        return accessibilityStore;
    }

    @computed
    get superscriptContext() {
        return !utils.typeChecks.isNullOrEmpty(this.props.superscriptContext)
            ? `-${this.props.superscriptContext}`
            : '';
    }

    /**
     * The superscript id field is generated with the footnoteLinks config flag is enabled
     *
     * @readonly
     */
    @computed
    get areLinksEnabled() {
        const { superscripts } = this.props;
        return superscripts.some(sup => sup.id !== undefined);
    }

    /**
     * Here we append a generated guid to help identify duplicate superscripts across the fund center.
     *
     * @readonly
     */
    @computed
    get superscriptsWithReferences(): Array<SuperscriptMetaWithRef> {
        const { superscripts } = this.props;
        if (!this.areLinksEnabled) {
            return superscripts;
        }
        return superscripts.map(sup => {
            return {
                ...sup,
                id: `${sup.id}${this.superscriptContext}`,
                ref: `-ref-${generateGuid()}`,
            };
        });
    }

    @computed
    get superscripts() {
        const superscripts = this.superscriptsWithReferences.map(
            (sup, index) => {
                if (this.areLinksEnabled) {
                    const ariaLabel =
                        this.accessibilityStore &&
                        this.accessibilityStore.superscriptAccessibilityValue(
                            'superscriptLink',
                            'Navigate to footnote superscript disclaimer {superscript}',
                            {
                                superscript: sup.superscript,
                            }
                        );
                    return (
                        <a
                            key={`sup-${index}`}
                            href={`#`}
                            id={`${sup.id}${sup.ref}`}
                            aria-label={ariaLabel}
                            aria-describedby={`#${sup.id}`}
                            onClick={e => this.onSuperscriptClick(e, sup)}
                            onKeyUp={e => this.onSuperscriptKeyUp(e, sup)}
                            onKeyDown={e =>
                                KeyboardEventHelper.disableAcceptKeys(e)
                            }
                        >
                            [{sup.superscript}]
                        </a>
                    );
                }
                return sup.superscript;
            }
        );
        if (!this.areLinksEnabled) {
            return superscripts.join(', ');
        }
        return superscripts;
    }

    componentWillUnmount() {
        if (this.areLinksEnabled) {
            this.superscriptsWithReferences.forEach(sup =>
                SuperscriptRefHelper.removeRefWhere(sup.id, sup.ref)
            );
        }
    }

    onSuperscriptKeyUp(
        e: React.KeyboardEvent<HTMLAnchorElement>,
        superscriptMeta: SuperscriptMetaWithRef
    ) {
        if (KeyboardEventHelper.isAcceptKey(e)) {
            this.scrollToFootnote(e, superscriptMeta);
        }
    }

    onSuperscriptClick(
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        superscriptMeta: SuperscriptMetaWithRef
    ) {
        this.scrollToFootnote(e, superscriptMeta);
    }

    scrollToFootnote(
        e:
            | React.MouseEvent<HTMLAnchorElement, MouseEvent>
            | React.KeyboardEvent<HTMLAnchorElement>,
        superscriptMeta: SuperscriptMetaWithRef
    ) {
        if (superscriptMeta.id) {
            e.stopPropagation();
            e.preventDefault();
            SuperscriptRefHelper.assignRef(
                superscriptMeta.id,
                superscriptMeta.ref
            );
            SuperscriptRefHelper.focusFootnote(superscriptMeta.id);

            const { dataStore, entityStore, analyticsContext } = this.props;
            dataStore.logDataLayer('superscript_navigate_to_footnote', {
                source: analyticsContext?.source,
                sourceContext: analyticsContext?.sourceContext,
                superscript: superscriptMeta.superscript,
                superscriptCode: superscriptMeta.type,
                relatedEntityId: entityStore?.value?.clientCode || 'Global',
            });
        }
    }

    render() {
        const { theme } = this.props;
        return <Sup {...theme.Superscript}>{this.superscripts}</Sup>;
    }
}

export default withTheme(Superscript);



// WEBPACK FOOTER //
// ./src/Components/Superscript/index.tsx